import { Logger, Utils, StringUtils, ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../../ComponentTypes";
import { Tweens } from "../../Effects/Tweens";
import { SwitchStyle } from "../Styles/SwitchStyle";
import { StyleManager } from "../../Managers/StyleManager";
import { IElement } from "../Interfaces/IElement";
import { Attributes } from "../../../Utils/Attributes";

/**
 * ShapeElement allows you to display vector art in the display list.
 * Example:
 *   <ShapeElement Class="ShapeElement" X="0" Y="0" Width="100" Height="100" Color="FF0000" Mask="ShapeElement" />
 *
 * @class ShapeElement
 * @extends PIXI.Graphics
 * @implements IElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ShapeElement extends PIXI.Graphics implements IElement {
    private _uid: string;
    public _color: number = 0xff0000;
    public _width: number;
    public _height: number;
    public _lineWidth: number;
    public _lineColor: number;
    public _fillAlpha: number;
    public points: PIXI.Point[] = [];
    public _draw: string = "";
    private _radius: number;

    protected style: SwitchStyle;
    private _tweenEngine: string;

    constructor() {
        super();

        this._uid = `${this.componentName}_${Utils.generateUuid()}`;
    }

    get tweenEngine(): string {
        return this._tweenEngine;
    }

    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        Tweens.removeTweens(this);
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element): void {
        Tweens.removeTweens(this);

        if (this.name == null) {
            this.name = node.nodeName;
        }

        if (node.hasAttribute("Style")) {
            this.style = <SwitchStyle>StyleManager.Styles[node.getAttribute(ShapeElementAttributes.STYLE)];
            if (this.style != null) {
                //console.log('test shapeElement style');
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name: " + node.getAttribute(ShapeElementAttributes.STYLE));
                }
            }
        }

        if (node.hasAttribute(ShapeElementAttributes.WIDTH)) {
            this._width = parseInt(node.getAttribute(ShapeElementAttributes.WIDTH));
        } else {
            if (DEBUG) {
                this.log("ERROR " + ShapeElementAttributes.WIDTH + " attribute doesn't exits");
            }
        }

        if (node.hasAttribute(ShapeElementAttributes.HEIGHT)) {
            this._height = parseInt(node.getAttribute(ShapeElementAttributes.HEIGHT));
        } else {
            if (DEBUG) {
                this.log("ERROR " + ShapeElementAttributes.HEIGHT + " attribute doesn't exits");
            }
        }

        this._color = Attributes.assignIntAttribute(node, ShapeElementAttributes.COLOR, this._color, 16);
        this._fillAlpha = Attributes.assignFloatAttribute(node, ShapeElementAttributes.FILL_ALPHA, this._fillAlpha);
        this._lineWidth = Attributes.assignIntAttribute(node, ShapeElementAttributes.LINE_WIDTH, this._lineWidth);
        this._lineColor = Attributes.assignIntAttribute(node, ShapeElementAttributes.LINE_COLOR, this._lineColor, 16);

        if (node.hasAttribute(ShapeElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE_X));
        }

        if (node.hasAttribute(ShapeElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE));
        }

        if (node.hasAttribute(ShapeElementAttributes.X)) {
            this.position.x = parseInt(node.getAttribute(ShapeElementAttributes.X));
        }

        if (node.hasAttribute(ShapeElementAttributes.Y)) {
            this.position.y = parseInt(node.getAttribute(ShapeElementAttributes.Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.DRAW)) {
            this.clear();
            this.beginFill(this._color, this._fillAlpha);
            this.lineStyle(this._lineWidth, this._lineColor);
            this.draw(node, node.getAttribute(ShapeElementAttributes.DRAW));
            this.endFill();
        } else {
            // This block is for backwards compatibility
            if (node.hasAttribute(ShapeElementAttributes.WIDTH)) {
                this._width = parseInt(node.getAttribute(ShapeElementAttributes.WIDTH));
            } else {
                if (DEBUG) {
                    this.log("ERROR " + ShapeElementAttributes.WIDTH + " attribute doesn't exits");
                }
            }

            if (node.hasAttribute(ShapeElementAttributes.HEIGHT)) {
                this._height = parseInt(node.getAttribute(ShapeElementAttributes.HEIGHT));
            } else {
                if (DEBUG) {
                    this.log("ERROR " + ShapeElementAttributes.HEIGHT + " attribute doesn't exits");
                }
            }

            if (this._width != null && this._height != null) {
                this.clear();
                this.beginFill(this._color, this._fillAlpha);
                this.lineStyle(this._lineWidth, this._lineColor);
                this.drawRect(0, 0, this._width, this._height);
                this.endFill();
            }
        }

        if (node.hasAttribute(ShapeElementAttributes.VISIBLE)) {
            this.visible = StringUtils.parseBoolean(node.getAttribute(ShapeElementAttributes.VISIBLE));
        }

        /* todo Doplnit PerfDebug */
        /*if (DEBUG) {
         if (PERFDEBUG_ENABLED) {
         PerfDebug.createElement(this, this.getDebugInfo());
         }
         }*/

        this.updateByNode(node);
    }

    /**
     * @todo Najst miesta, kde sa vola metoda create(). Ak by bolo vhodne, aby sa tam volala metoda update(), treba volat ju. Tomu treba prisposobit aj obsah metody.
     *
     * @param node
     */
    public updateByNode(node: Element): void {
        if (node.hasAttribute(ShapeElementAttributes.X)) {
            this.position.x = parseInt(node.getAttribute(ShapeElementAttributes.X));
        }

        if (node.hasAttribute(ShapeElementAttributes.Y)) {
            this.position.y = parseInt(node.getAttribute(ShapeElementAttributes.Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.ALPHA)) {
            this.alpha = parseFloat(node.getAttribute(ShapeElementAttributes.ALPHA));
        }

        if (node.hasAttribute(ShapeElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(ShapeElementAttributes.MASK)) {
                    let shapeElement: ShapeElement = new ShapeElement();
                    shapeElement.create(next);
                    this.mask = shapeElement;
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(ShapeElementAttributes.MASK));
                }
            }
        }

        if (node.hasAttribute(ShapeElementAttributes.ALPHA_TWEEN)) {
            let alphaTween: Array<number> = ArrayUtils.numberArray(
                node.getAttribute(ShapeElementAttributes.ALPHA_TWEEN),
            );
            let ease: any;

            if (alphaTween != null) {
                ease = null;
                if (node.hasAttribute(ShapeElementAttributes.ALPHA_TWEEN_EASE)) {
                    ease = node.getAttribute(ShapeElementAttributes.ALPHA_TWEEN_EASE);
                }

                let tweenDelay: number = null;
                if (node.hasAttribute(ShapeElementAttributes.ALPHA_TWEEN_DELAY)) {
                    tweenDelay = parseInt(node.getAttribute(ShapeElementAttributes.ALPHA_TWEEN_DELAY));
                }

                let tweenDuration: number = null;
                if (node.hasAttribute(ShapeElementAttributes.ALPHA_TWEEN_DURATION)) {
                    tweenDuration = parseInt(node.getAttribute(ShapeElementAttributes.ALPHA_TWEEN_DURATION));
                }

                Tweens.alphaTween(this, {
                    alphas: alphaTween,
                    tweenEase: ease,
                    tweenDuration: tweenDuration,
                    tweenDelay: tweenDelay,
                });
            } else {
                if (DEBUG) {
                    this.log("WARNING wrong parameters for " + ShapeElementAttributes.ALPHA_TWEEN);
                }
            }
        }
    }

    /**
     * Draw object by type.
     * @param node
     * @param {string} objectType
     */
    private draw(node: Element, objectType: string) {
        switch (objectType) {
            case "Circle":
                this.drawCircle(0, 0, this._radius);
                break;
            case "Ellipse":
                this.drawEllipse(0, 0, this._width, this._height);
                break;
            case "Polygon":
                const path = node.hasAttribute("Path") ? ArrayUtils.stringArray(node.getAttribute("Path"), "|") : [];
                let points: PIXI.Point[] = [];
                for (let i = 0; i < path.length; i++) {
                    const point = ArrayUtils.numberArray(path[i]);
                    points.push(new PIXI.Point(point[0], point[1]));
                }
                this.drawPolygon(points);
                break;
            case "Rect":
                this.drawRect(0, 0, this._width, this._height);
                break;
            case "RoundedRect":
                this.drawRoundedRect(0, 0, this._width, this._height, this._radius);
                break;
            default:
                if (DEBUG) {
                    this.log("ERROR objectType: " + objectType + " not supported.");
                }
                break;
        }
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @retutn void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param s
     */
    protected logError(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }
}

export const ShapeElementAttributes = {
    /**
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",

    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    /**
     * Shape's fill color.
     * @property Color
     * @type hexadecimal
     * @default ff0000
     */
    COLOR: "Color",
    DRAW: "Draw",
    DEBUG: "Debug",
    FILL_ALPHA: "FillAlpha",
    /**
     * Shape's height.
     * @property Height
     * @type number
     * @default null
     */
    HEIGHT: "Height",
    LINE_COLOR: "LineColor",
    LINE_WIDTH: "LineWidth",
    /**
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type string
     * @default null
     */
    MASK: "Mask",
    /**
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /**
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_DELAY: "PositionTweenDelay",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },
    POSITION_TWEEN_LOOP: "PositionTweenLoop",
    /**
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    ROTATION_TWEEN_LOOP: "RotationTweenLoop",
    RADIUS: "Radius",
    /**
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    SCALE_X: "ScaleX",
    /**
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_DELAY: "ScaleTweenDelay",
    /**
     * @deprecated Use SCALE_TWEEN instead SCALE_TWEEN_DURATION
     */
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_LOOP: "ScaleTweenLoop",
    SCALE_TWEEN_X: "ScaleTweenX",
    SCALE_TWEEN_Y: "ScaleTweenY",
    STYLE: "Style",
    /**
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /**
     * Shape's width.
     * @property Width
     * @type number
     * @default null
     */
    WIDTH: "Width",
    /**
     * The X (horizontal) position of the shape, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /**
     * The Y (vertical) position of the shape, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ShapeElement.ts
 **/